/**
 * computeClusters.ts -- MER Algo Footprints
 *
 * Phase 2 v1: pure function that buckets off-exchange Trade prints into
 * dark-pool clusters.
 *
 * Algorithm:
 *
 *   1. Filter trades to isOffExchange === true.
 *
 *   2. Bucket size is max(0.1% of current price, $0.05). For MU at $685
 *      that's ~$0.69; for IREN at $50 it's $0.05. The 0.1% floor scales
 *      naturally across price ranges; the $0.05 absolute floor prevents
 *      degenerate bucket sizes on penny-stock symbols.
 *
 *   3. Map prints into buckets by floor(price / bucketSize).
 *
 *   4. A bucket is a "significant cluster" if:
 *        printCount >= 3 AND totalShares >= 1500
 *      Rationale: 1500 shares ~= 3 blocks worth (BLOCK_THRESHOLD = 500 in
 *      the flow engine), so a cluster represents at least 3 prints AND
 *      meaningful aggregate size. Conservative thresholds chosen to keep
 *      v1 signal-to-noise high. Tunable from production attribution data.
 *
 *   5. Sort clusters by totalShares descending, return top 8. The cap
 *      bounds downstream work and keeps the AI prompt section concise.
 *
 *   6. `partial` from upstream is passed through unchanged. Cluster
 *      confidence is the consumer's responsibility (liquidityEngine in
 *      v1, others later).
 *
 * Determinism:
 *
 *   Pure function. Given the same Trade[] input, returns the same
 *   PrintClusterSnapshot output (modulo `timestamp` which is set by the
 *   caller, see runtime).
 */

import type { Trade } from '../../marketData/schemas/tradeSchema';
import type { DarkPoolPrintCluster, PrintClusterSnapshot } from './schema';

const MIN_PRINT_COUNT  = 3;
const MIN_TOTAL_SHARES = 1500;
const MAX_CLUSTERS     = 8;
// Bucket size: 0.15% of current price, floored at $0.05. Widened from
// 0.10% during v1 smoke testing after a bucket-boundary artifact surfaced
// (prints separated by a fraction of a bucket-width get split across
// adjacent buckets and fail to cluster). 0.15% reduces the false-miss
// rate without adding sliding-window complexity. For MU at $685 the
// bucket is ~$1.03; for SPY at $585 it's ~$0.88. The $0.05 floor
// preserves sensible buckets on low-priced symbols.
//
// Known v1 limitation: bucket-edge sensitivity remains. Prints right at
// a bucket boundary still get split. Acceptable for v1 because the
// downstream liquidityEngine boost uses tolerance * 0.5 (typically
// ~0.5%) when matching clusters to zones, which absorbs most residual
// drift. Revisit in v2 with a sliding-window aggregator if production
// attribution shows cluster misses materially affect zone confirmation.
const BUCKET_PCT       = 0.0015;  // 0.15% of price
const BUCKET_FLOOR     = 0.05;    // $0.05 absolute minimum

function bucketSizeFor(currentPrice: number): number {
  return Math.max(currentPrice * BUCKET_PCT, BUCKET_FLOOR);
}

/**
 * Bucket midpoint for a given price under a given bucket size. Using the
 * midpoint (not the floor edge) keeps the cluster.price field intuitive
 * for downstream display and zone-matching.
 */
function bucketMidpoint(price: number, bucketSize: number): number {
  const idx = Math.floor(price / bucketSize);
  return (idx + 0.5) * bucketSize;
}

interface BucketAccum {
  printCount:   number;
  totalShares:  number;
  largestPrint: number;
  firstTs:      number;
  lastTs:       number;
}

/**
 * Compute dark-pool print clusters for a given trade window.
 *
 * @param symbol    Upper-cased ticker
 * @param trades    All trades in the window (off-exchange filtering done internally)
 * @param fromMs    Window start (Unix ms)
 * @param toMs      Window end   (Unix ms)
 * @param partial   Upstream partial-data flag (Polygon proxy truncation)
 */
export function computeClusters(
  symbol:  string,
  trades:  Trade[],
  fromMs:  number,
  toMs:    number,
  partial: boolean = false,
): PrintClusterSnapshot {
  const sym = String(symbol || '').toUpperCase();
  const emptySnapshot: PrintClusterSnapshot = {
    symbol:    sym,
    clusters:  [],
    partial,
    windowMs:  { from: fromMs, to: toMs },
    timestamp: Date.now(),
  };

  if (!Array.isArray(trades) || trades.length === 0) return emptySnapshot;

  // Off-exchange filter. The institutional flow engine already uses
  // Trade.isOffExchange; we apply the same predicate so cluster output is
  // congruent with the flow signal's view of dark-pool activity.
  const offEx = trades.filter(t => t && t.isOffExchange === true);
  if (offEx.length === 0) return emptySnapshot;

  // Use the median off-exchange price as the reference for bucket sizing.
  // Median is more robust than mean against outlier prints (e.g. a stray
  // crossed-market print far from the active range).
  const sortedPrices = offEx.map(t => t.price).sort((a, b) => a - b);
  const median = sortedPrices[Math.floor(sortedPrices.length / 2)];
  if (!isFinite(median) || median <= 0) return emptySnapshot;

  const bucketSize = bucketSizeFor(median);

  // Aggregate into buckets keyed by floor(price / bucketSize).
  const buckets = new Map<number, BucketAccum>();
  for (const t of offEx) {
    if (!isFinite(t.price) || t.price <= 0 || t.size <= 0) continue;
    const key = Math.floor(t.price / bucketSize);
    const existing = buckets.get(key);
    if (existing) {
      existing.printCount  += 1;
      existing.totalShares += t.size;
      if (t.size > existing.largestPrint) existing.largestPrint = t.size;
      if (t.timestamp < existing.firstTs) existing.firstTs = t.timestamp;
      if (t.timestamp > existing.lastTs)  existing.lastTs  = t.timestamp;
    } else {
      buckets.set(key, {
        printCount:   1,
        totalShares:  t.size,
        largestPrint: t.size,
        firstTs:      t.timestamp,
        lastTs:       t.timestamp,
      });
    }
  }

  // Filter to significant clusters and materialize.
  const clusters: DarkPoolPrintCluster[] = [];
  for (const [key, b] of buckets.entries()) {
    if (b.printCount < MIN_PRINT_COUNT)  continue;
    if (b.totalShares < MIN_TOTAL_SHARES) continue;
    clusters.push({
      price:        bucketMidpoint(key * bucketSize, bucketSize),
      printCount:   b.printCount,
      totalShares:  b.totalShares,
      largestPrint: b.largestPrint,
      firstTs:      b.firstTs,
      lastTs:       b.lastTs,
    });
  }

  // Sort by totalShares desc; cap at MAX_CLUSTERS.
  clusters.sort((a, b) => b.totalShares - a.totalShares);
  const top = clusters.slice(0, MAX_CLUSTERS);

  return {
    symbol:    sym,
    clusters:  top,
    partial,
    windowMs:  { from: fromMs, to: toMs },
    timestamp: Date.now(),
  };
}

// Exported for testing / downstream tolerance matching.
export const _internal = {
  bucketSizeFor,
  bucketMidpoint,
  MIN_PRINT_COUNT,
  MIN_TOTAL_SHARES,
  MAX_CLUSTERS,
  BUCKET_PCT,
  BUCKET_FLOOR,
};
